import { posthog } from '@pique/core/posthog';
import type { Props as JoyrideProps, Step } from 'react-joyride';

export interface UserPreferences {
  usage?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  role?: string;
  detailLevel?: string;
  nudges?: string;
  noteStyle?: string;
  displayMode?: string;
}

export function syncUserPreferencesToPostHog(preferences: UserPreferences) {
  const properties: Record<string, unknown> = {};

  if (preferences.usage) properties['pref_usage'] = preferences.usage;
  if (preferences.firstName) properties['pref_first_name'] = preferences.firstName;
  if (preferences.lastName) properties['pref_last_name'] = preferences.lastName;
  if (preferences.company) properties['pref_company'] = preferences.company;
  if (preferences.role) properties['pref_role'] = preferences.role;
  if (preferences.detailLevel)
    properties['pref_detail_level'] = preferences.detailLevel;
  if (preferences.nudges) properties['pref_nudges'] = preferences.nudges;
  if (preferences.noteStyle) properties['pref_note_style'] = preferences.noteStyle;
  if (preferences.displayMode)
    properties['pref_display_mode'] = preferences.displayMode;

  if (Object.keys(properties).length > 0) {
    posthog.setPersonProperties(properties);
  }
}

export function syncAllUserPreferencesFromStorage() {
  const preferences: UserPreferences = {};

  try {
    const detailLevel = localStorage.getItem('pique-detail-level-preference');
    if (detailLevel) preferences.detailLevel = JSON.parse(detailLevel);

    const nudges = localStorage.getItem('pique-nudges-preference');
    if (nudges) preferences.nudges = JSON.parse(nudges);

    const noteStyle = localStorage.getItem('pique-note-style-preference');
    if (noteStyle) preferences.noteStyle = JSON.parse(noteStyle);
  } catch {
    // Silent fail on parse errors
  }

  syncUserPreferencesToPostHog(preferences);
}

export interface AutoAdvanceConfig {
  duration: number;
  showProgressBar?: boolean;
}

export interface PiqueOnboardingStep extends Omit<Step, 'content'> {
  id: string;
  content?: Step['content'];
  category?: string;
  requiresInteraction?: boolean;
  actionText?: string;
  isTauriOnly?: boolean;
  autoAdvance?: AutoAdvanceConfig;
}

export interface OnboardingTour {
  id: string;
  name: string;
  description: string;
  steps: PiqueOnboardingStep[];
}

export const STORAGE_KEYS = {
  SURVEY_SHOWN: 'pique_survey_shown',
  ONBOARDING_SHOWN: 'pique_onboarding_shown',
  ONBOARDING_IN_PROGRESS: 'pique_onboarding_in_progress',
  ONBOARDING_STEP_INDEX: 'pique_onboarding_step_index',
  POST_AUGMENTATION_SHOWN: 'pique_post_augmentation_tour_shown',
  POST_AUGMENTATION_IN_PROGRESS: 'pique_post_augmentation_in_progress',
  POST_AUGMENTATION_STEP_INDEX: 'pique_post_augmentation_step_index',
  POST_AUGMENTATION_PENDING: 'pique_post_augmentation_tour_pending',
} as const;

export const DEFAULT_JOYRIDE_PROPS: Partial<JoyrideProps> = {
  continuous: true,
  showProgress: false,
  showSkipButton: false,
  disableOverlay: true,
  disableOverlayClose: false,
  disableScrolling: false,
  hideCloseButton: true,
  scrollToFirstStep: true,
  scrollDuration: 400,
  scrollOffset: 20,
  spotlightClicks: true,
  spotlightPadding: 12,
  floaterProps: {
    hideArrow: true,
    offset: 12,
  },
  styles: {
    options: {
      overlayColor: 'rgba(0, 0, 0, 0.4)',
      primaryColor: '#8D5CF5',
      spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
      zIndex: 9999,
    },
  },
  locale: {
    back: 'Previous',
    close: 'Close Tour',
    last: 'Finish',
    next: 'Next',
    nextLabelWithProgress: 'Next ({step} of {steps})',
    skip: 'Skip Tour',
  },
};
